import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { Upload, Heart, Camera, Send, Play, MapPin } from 'lucide-react';

/**
 * Reusable Instagram-style social feed.
 *
 * Props:
 *   feedKey: 'youngbuzz' | 'owner' | 'patron' — determines API path
 *   tokenKey: localStorage key for the auth token ('token' or 'bar_owner_token')
 *   accentColor: primary color hex
 *   glowColor: secondary color hex
 *   title: section title text
 *   venueOptions: optional array of { id, name } — enables venue dropdown (patron only)
 *   defaultVenueTag: optional string prefilled venue label (owner: auto-tagged bar name)
 *   showLike: whether to render like button (yes for youngbuzz + patron, hide for owner)
 *   allowFetchOnMount: default true
 */
export default function SocialFeed({
  feedKey = 'patron',
  tokenKey = 'token',
  accentColor = '#FF4444',
  glowColor = '#CC0000',
  title = '📸 Social Feed',
  venueOptions = null,
  defaultVenueTag = '',
  showLike = true,
}) {
  const [posts, setPosts] = useState([]);
  const [caption, setCaption] = useState('');
  const [venueId, setVenueId] = useState('');
  const [venueTag, setVenueTag] = useState(defaultVenueTag);
  const [file, setFile] = useState(null);
  const [posting, setPosting] = useState(false);
  const inputRef = useRef(null);

  const authAxios = () => {
    const token = localStorage.getItem(tokenKey);
    return axios.create({
      baseURL: API,
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
  };

  const BACKEND_ORIGIN = API.replace(/\/api$/, '');

  const load = async () => {
    try {
      const path = feedKey === 'youngbuzz' ? '/youngbuzz/posts'
                 : feedKey === 'owner' ? '/owner/posts'
                 : '/patron/posts';
      const res = await authAxios().get(path);
      setPosts(res.data || []);
    } catch (err) { /* silent */ }
  };

  useEffect(() => { load(); }, [feedKey]);

  const handlePost = async () => {
    if (!file) return toast.error('Attach a photo or video');
    setPosting(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('caption', caption);
    if (feedKey === 'youngbuzz') fd.append('venue_tag', venueTag);
    if (feedKey === 'patron') fd.append('venue_id', venueId);
    try {
      const path = feedKey === 'youngbuzz' ? '/youngbuzz/posts'
                 : feedKey === 'owner' ? '/owner/posts'
                 : '/patron/posts';
      await authAxios().post(path, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Posted!');
      setFile(null); setCaption(''); setVenueId(''); if (inputRef.current) inputRef.current.value = '';
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Post failed');
    } finally {
      setPosting(false);
    }
  };

  const handleLike = async (id) => {
    try {
      const path = feedKey === 'youngbuzz'
        ? `/youngbuzz/posts/${id}/like`
        : `/patron/posts/${id}/like`;
      await axios.post(`${API}${path}`);
      setPosts(ps => ps.map(p => p.id === id ? { ...p, likes: (p.likes || 0) + 1 } : p));
    } catch (err) { /* silent */ }
  };

  const cardStyle = {
    background: 'rgba(12, 12, 16, 0.85)',
    border: `1px solid ${accentColor}66`,
    boxShadow: `0 0 15px ${glowColor}33`,
  };

  return (
    <Card
      data-testid={`social-feed-${feedKey}`}
      className="p-5 mt-4"
      style={cardStyle}
    >
      <h2 className="text-xl font-bold mb-4" style={{ color: accentColor, textShadow: `0 0 10px ${glowColor}` }}>
        {title}
      </h2>

      {/* Composer */}
      <div className="p-3 rounded-lg mb-5 space-y-2" style={{
        background: 'rgba(0,0,0,0.5)',
        border: `1px dashed ${accentColor}88`
      }}>
        <div className="flex flex-col gap-2">
          <label className="cursor-pointer flex items-center gap-2 text-sm" style={{ color: accentColor }}>
            <Upload className="w-4 h-4" />
            {file ? file.name : 'Attach photo/video (max 50MB)'}
            <input
              ref={inputRef}
              data-testid={`${feedKey}-file-input`}
              type="file"
              accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime"
              onChange={(e) => setFile(e.target.files?.[0])}
              className="hidden"
            />
          </label>
          {feedKey === 'patron' && venueOptions && (
            <select
              data-testid="patron-post-venue"
              value={venueId}
              onChange={(e) => setVenueId(e.target.value)}
              className="w-full text-sm rounded p-2"
              style={{ background: '#0a0a0a', border: `1px solid ${accentColor}66`, color: 'white' }}
            >
              <option value="">Select venue (optional)</option>
              {venueOptions.map(v => (
                <option key={v.id} value={v.id}>{v.name}</option>
              ))}
            </select>
          )}
          {feedKey === 'youngbuzz' && (
            <Input
              data-testid="yb-venue-tag"
              placeholder="Tag venue (e.g. Riverside Game Lab)"
              value={venueTag}
              onChange={(e) => setVenueTag(e.target.value)}
              style={{ background: '#0a0a0a', border: `1px solid ${accentColor}66`, color: 'white' }}
            />
          )}
          <Textarea
            data-testid={`${feedKey}-post-caption`}
            placeholder="Say something..."
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            className="text-sm"
            style={{ background: '#0a0a0a', border: `1px solid ${accentColor}66`, color: 'white', minHeight: 60 }}
            maxLength={500}
          />
          <Button
            data-testid={`${feedKey}-post-btn`}
            onClick={handlePost}
            disabled={posting || !file}
            className="font-bold"
            style={{ background: accentColor, color: '#000' }}
          >
            <Send className="w-4 h-4 mr-2" />
            {posting ? 'Posting...' : 'Post'}
          </Button>
        </div>
      </div>

      {/* Feed grid */}
      {posts.length === 0 ? (
        <p className="text-sm text-slate-500 text-center py-4">Nothing here yet. Be the first to post!</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
          {posts.map(p => {
            const src = p.image_url?.startsWith('http') ? p.image_url : `${BACKEND_ORIGIN}${p.image_url}`;
            return (
              <div
                key={p.id}
                data-testid={`post-${p.id}`}
                className="rounded-lg overflow-hidden"
                style={{
                  background: 'rgba(0,0,0,0.7)',
                  border: `1px solid ${accentColor}44`,
                  boxShadow: `0 0 10px ${glowColor}22`
                }}
              >
                <div className="relative aspect-square bg-black">
                  {p.media_type === 'video' ? (
                    <>
                      <video src={src} className="w-full h-full object-cover" muted preload="metadata" />
                      <Play className="absolute inset-0 m-auto w-10 h-10 text-white/80" />
                    </>
                  ) : (
                    <img src={src} alt="" className="w-full h-full object-cover" />
                  )}
                </div>
                <div className="p-2 space-y-1">
                  {p.caption && <p className="text-xs text-white/90 line-clamp-2">{p.caption}</p>}
                  <div className="flex items-center justify-between">
                    {(p.venue || p.venue_tag) && (
                      <span className="text-[10px] flex items-center gap-1" style={{ color: accentColor }}>
                        <MapPin className="w-3 h-3" /> {p.venue || p.venue_tag}
                      </span>
                    )}
                    {p.user_name && (
                      <span className="text-[10px] text-slate-400">@{p.user_name}</span>
                    )}
                    {showLike ? (
                      <button
                        data-testid={`like-${p.id}`}
                        onClick={() => handleLike(p.id)}
                        className="flex items-center gap-1 text-xs hover:scale-110 transition"
                        style={{ color: accentColor }}
                      >
                        <Heart className="w-3 h-3" fill="currentColor" />
                        {p.likes || 0}
                      </button>
                    ) : (
                      <span className="text-[10px] text-slate-400">👁 {p.views || 0}</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
